import { GetStaticPaths, GetStaticPathsResult, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { getAlgolia } from '@boss/algolia-client';
import { CHANNEL, contenfulOverviewTypes, overviewPageSlugs } from '@boss/constants/b2b-b2c';
import { contentfulContext } from '@boss/contentful-client';
import { content as contentService } from '@boss/services';
import { LOCALE_CODE } from '@boss/types/b2b-b2c';

import { InstantSearch, SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import { OverviewPage as OverviewPageContainer } from '../../containers';
import Layout from '../../layout/default';
import { overviewPageTypes } from '../../utils';
import { getContenPagesIndexName } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocaleMarket, mapNextToContentfulMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const config = {
  maxDuration: 30,
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const paths = overviewPageTypes.reduce((accumulator, type) => {
    const slugs = overviewPageSlugs[type as keyof typeof overviewPageSlugs];

    Object.keys(slugs)
      .filter(locale => locales?.includes(locale))
      .forEach(locale => {
        const slug = slugs[locale];

        accumulator.push({
          params: { slug },
          locale,
        });
      });

    return accumulator;
  }, [] as GetStaticPathsResult['paths']);

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps = async ({ locale, preview, params }: GetStaticPropsContext) => {
  const slug = params?.slug as string;

  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const overviewPageType = Object.keys(overviewPageSlugs).find(type => {
    const slugs = overviewPageSlugs[type as keyof typeof overviewPageSlugs];

    return Object.values(slugs).includes(slug);
  });

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const market = mapNextToContentfulMarket(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  // SET GLOBAL MARKET CONTEXT
  contentfulContext.market = market;

  const [navigation, overviewPage] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocaleMarket,
      channel: CHANNEL,
      type: contenfulOverviewTypes[overviewPageType as keyof typeof contenfulOverviewTypes],
      batchMode: IS_BUILD,
      preview: !!preview,
      market,
    }),
  ]);

  return {
    props: {
      pageType: PageTypes.OVERVIEW_PAGE,
      slug,
      navigation,
      pageData: overviewPage,
      overviewPageType,
      preview: !!preview,
      market,
      locale: locale as LOCALE_CODE,
      localizedSlugs: overviewPageSlugs[overviewPageType as keyof typeof overviewPageSlugs],
      ...(await getServerSideTranslations(locale, ['common', 'forms', 'overview-page'])),
    },
  };
};

const OverviewPage = ({
  preview,
  pageData,
  navigation,
  localizedSlugs,
  overviewPageType: pageType,
  locale,
  slug,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('overview-page');
  const searchClient = getAlgolia({ isShared: true }).algoliaClient;
  const indexName = getContenPagesIndexName(locale);
  const pageTitle = t('title') ?? '';

  return (
    <SEO
      description={pageData?.metaDescription}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      noFollow={pageData?.noFollow}
      noIndex={pageData?.noIndex}
      title={pageData?.pageTitle ?? pageTitle}
    >
      <Layout localizedSlugs={localizedSlugs} navigation={navigation} preview={preview}>
        <InstantSearch
          disableRouting
          facetFilters={`channel:"${CHANNEL}" AND tags:"${pageType}"`}
          indexName={indexName}
          searchClient={searchClient}
        >
          <OverviewPageContainer cmsData={pageData} indexName={indexName} overviewPageSlug={slug} />
        </InstantSearch>
      </Layout>
    </SEO>
  );
};

export default OverviewPage;
